import type { WorkShiftCell } from "@senciamatch/shared/models/workShiftCell";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

// シンプルな変更情報
export interface ShiftChange {
  workShiftId: string;
  originalStaffShiftId: string | null;
  originalStaffName: string;
  newStaffShiftId: string | null;
  newStaffName: string;
  workShiftInfo: {
    buildingName: string;
    year: number;
    month: number;
    day: number;
    startHour: number | null;
    startMinute: number | null;
    endHour: number | null;
    endMinute: number | null;
  };
}

// シンプルなセッション情報
export interface SimpleEditSession {
  id: string;
  workShiftGroupId: string;
  changes: ShiftChange[];
  createdAt: Date;
  updatedAt: Date;
}

interface WorkShiftEditState {
  // 編集セッション管理
  sessions: SimpleEditSession[];
  currentSessionId: string | null;

  // アクション
  setSessions: (sessions: SimpleEditSession[]) => void;
  addSession: (session: SimpleEditSession) => void;
  removeSession: (sessionId: string) => void;
  setCurrentSession: (sessionId: string | null) => void;
  getCurrentSession: () => SimpleEditSession | null;
  clearSessions: () => void;

  // 変更管理アクション
  addChange: (sessionId: string, change: ShiftChange) => void;
  removeChange: (sessionId: string, workShiftId: string) => void;
  getChanges: (sessionId: string) => ShiftChange[];
  updateChange: (sessionId: string, workShiftId: string, change: ShiftChange) => void;
}

export const useWorkShiftEditStore = create<WorkShiftEditState>()(
  devtools(
    persist(
      (set, get) => ({
        sessions: [],
        currentSessionId: null,

        setSessions: (sessions: SimpleEditSession[]) => set(() => ({ sessions })),

        addSession: (session: SimpleEditSession) =>
          set((state) => ({
            // Add new session at the beginning of the array
            sessions: [{ ...session, changes: session.changes || [] }, ...state.sessions],
            currentSessionId: session.id,
          })),

        removeSession: (sessionId: string) =>
          set((state) => ({
            sessions: state.sessions.filter((s) => s.id !== sessionId),
            currentSessionId: state.currentSessionId === sessionId ? null : state.currentSessionId,
          })),

        setCurrentSession: (sessionId: string | null) => set(() => ({ currentSessionId: sessionId })),

        getCurrentSession: () => {
          const state = get();
          return state.sessions.find((s) => s.id === state.currentSessionId) || null;
        },

        clearSessions: () => set(() => ({ sessions: [], currentSessionId: null })),

        addChange: (sessionId: string, change: ShiftChange) =>
          set((state) => ({
            sessions: state.sessions.map((session) =>
              session.id === sessionId
                ? {
                    ...session,
                    changes: [...session.changes, change],
                    updatedAt: new Date(),
                  }
                : session,
            ),
          })),

        removeChange: (sessionId: string, workShiftId: string) =>
          set((state) => ({
            sessions: state.sessions.map((session) =>
              session.id === sessionId
                ? {
                    ...session,
                    changes: session.changes.filter((c) => c.workShiftId !== workShiftId),
                    updatedAt: new Date(),
                  }
                : session,
            ),
          })),

        getChanges: (sessionId: string) => {
          const state = get();
          const session = state.sessions.find((s) => s.id === sessionId);
          return session?.changes || [];
        },

        updateChange: (sessionId: string, workShiftId: string, change: ShiftChange) =>
          set((state) => ({
            sessions: state.sessions.map((session) =>
              session.id === sessionId
                ? {
                    ...session,
                    changes: session.changes.map((c) => (c.workShiftId === workShiftId ? change : c)),
                    updatedAt: new Date(),
                  }
                : session,
            ),
          })),
      }),
      {
        name: "sm-workShiftEditStore",
        // Dateオブジェクトの復元処理を追加
        onRehydrateStorage: () => (state) => {
          if (state?.sessions) {
            state.sessions = state.sessions.map((session) => ({
              ...session,
              createdAt: new Date(session.createdAt),
              updatedAt: new Date(session.updatedAt),
              changes: session.changes || [],
            }));
          }
        },
      },
    ),
  ),
);
